<template>
  <layout>
    <template v-slot:title>Document Browser</template>
    <div>
      <document-tree @item-select="itemSelected"></document-tree>
    </div>
    <template v-slot:modal>
      <action-modal
        v-if="showActionModal"
        :selected-item="selectedItem"
        @action="action"
      />
      <confirm-delete-modal
        v-if="showDeleteModal"
        :message="deleteMessage"
        @confirm="deleteConfirmed"
        @cancel="deleteCanceled"
      />
    </template>
  </layout>
</template>
<script>
import currentTemplates from "@/mixins/currentTemplates";
import documentModelMixin from "@/mixins/documentModel";
import DocumentTree from "@/components/document-browser/DocumentTree.vue";
import ActionModal from "@/components/document-browser/ActionModal.vue";
import ConfirmDeleteModal from "@/components/common/confirmations/ConfirmDeleteModal.vue";
import {
  SECTION,
  TAG,
  REPEATING_CONTENT_BLOCK,
  REPEATING_TABLE_BLOCK,
  HTML_CONTENT_BLOCK,
  CONTENT_BLOCK,
} from "@/constants/contentControlTypes";
import { BUSINESS_RULE } from "@/constants/businessRule";
import contentControls from "@/services/word/contentControlsService";
import customProperties from "@/services/word/customPropertiesService";
import contentBlocks from "@/services/sharedo/contentBlocksService";

const isContentControl = function(item) {
  return [
    SECTION,
    TAG,
    REPEATING_CONTENT_BLOCK,
    REPEATING_TABLE_BLOCK,
    HTML_CONTENT_BLOCK,
    CONTENT_BLOCK,
  ].some((type) => type === item.type);
};

const itemSelected = function(selectedItem) {
  this.selectedItem = selectedItem;
};

const _delete = function(selectedItem) {
  this.showDeleteModal = true;
};

const deleteCanceled = function() {
  this.showDeleteModal = false;
};

const deleteConfirmed = async function() {
  const model = this.documentModel;
  const item = this.selectedItem.item;
  const id = item.id;

  this.logDebug("Deleting item.", { item });

  try {
    if (isContentControl(this.selectedItem)) {
      await contentControls.delete(id);
      this.logDebug("Deleted content control.", { id });

      const contentControlModel = model.findContentControl(id);

      if (
        contentControlModel &&
        contentControlModel.type === HTML_CONTENT_BLOCK
      ) {
        const allMatchingBlocks = model.findContentControlBySystemName(
          contentControlModel.systemName
        );
        if (allMatchingBlocks.length === 1) {
          const sharedoId = await customProperties.get("SharedoActivityId");
          if (sharedoId && this.currentTemplates.length > 0) {
            const template = this.currentTemplates[0];
            await contentBlocks.markAsUnused(
              contentControlModel,
              sharedoId,
              template
            );

            this.logInfo("Marked content block as unused.", {
              contentControlModel,
              sharedoId,
              template,
            });
          }
        }
      }

      model.deleteContentControl(id);

      await this.saveDocumentModel(model);
      this.logDebug("Document model saved.", model);
    } else if (this.selectedItem.type === BUSINESS_RULE) {
      const rule = model.businessRules.find((r) => r.id === id);
      if (rule) {
        model.businessRules.splice(model.businessRules.indexOf(rule), 1);

        await this.saveDocumentModel(model);
        this.logDebug("Document model saved.", model);
      }
    }

    this.selectedItem = null;
    this.showDeleteModal = false;
  } catch (error) {
    this.logError("Error deleting item.", { itme, error });
    throw error;
  }
};

const action = function({ action, selectedItem }) {
  this.showActionModal = false;
  switch (action) {
    case "close":
    case "update":
      this.showActionModal = false;
      break;
    case "delete":
      this._delete(selectedItem);
      break;
  }
};

export default {
  components: { DocumentTree, ActionModal, ConfirmDeleteModal },
  mixins: [currentTemplates, documentModelMixin],
  data: function() {
    return {
      selectedItem: null,
      showDeleteModal: false,
      showActionModal: false,
    };
  },
  computed: {
    deleteMessage: function() {
      switch (this.selectedItem.type) {
        case SECTION:
          return "Are you sure you want to delete this section?";
        case BUSINESS_RULE:
          return "Are you sure you want to delete this rule?";
        case HTML_CONTENT_BLOCK:
          return "Are you sure you want to delete this content?";
        default:
          return "Are you sure you want to delete this item?";
      }
    },
  },
  watch: {
    selectedItem: function(newItem) {
      this.showActionModal = !!newItem;
    },
  },
  methods: {
    itemSelected,
    action,
    _delete,
    deleteCanceled,
    deleteConfirmed,
  },
};
</script>
